import { createTheme as createAmplifyTheme } from "@aws-amplify/ui-react";
import { alpha, darken, type Theme as MuiTheme } from "@mui/material/styles";

import { bodyFontFamily } from "./mtgTheme";

const createColorScale = (color: string, isDarkMode: boolean) => ({
  10: { value: alpha(color, isDarkMode ? 0.18 : 0.08) },
  20: { value: alpha(color, isDarkMode ? 0.28 : 0.14) },
  40: { value: alpha(color, isDarkMode ? 0.42 : 0.24) },
  60: { value: alpha(color, isDarkMode ? 0.62 : 0.38) },
  80: { value: color },
  90: { value: darken(color, isDarkMode ? 0.08 : 0.14) },
  100: { value: darken(color, isDarkMode ? 0.18 : 0.24) },
});

const createNeutralScale = (isDarkMode: boolean) =>
  isDarkMode
    ? {
        10: { value: "#121212" },
        20: { value: "#1B1B1B" },
        40: { value: "#2C2C2C" },
        60: { value: "#4B5563" },
        80: { value: "#9CA3AF" },
        90: { value: "#D1D5DB" },
        100: { value: "#F3F4F6" },
      }
    : {
        10: { value: "#FFFFFF" },
        20: { value: "#F4F1E8" },
        40: { value: "#E4DCCA" },
        60: { value: "#C1B49A" },
        80: { value: "#6B645A" },
        90: { value: "#4B5563" },
        100: { value: "#1B1B1B" },
      };

const createFocusShadow = (color: string) => ({
  value: {
    offsetX: "0px",
    offsetY: "0px",
    blurRadius: "0px",
    spreadRadius: "2px",
    color,
  },
});

const createOverlayScale = (isDarkMode: boolean) => ({
  5: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.05) },
  10: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.1) },
  20: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.2) },
  30: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.3) },
  40: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.4) },
  50: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.5) },
  60: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.6) },
  70: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.7) },
  80: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.8) },
  90: { value: alpha(isDarkMode ? "#FFFFFF" : "#000000", 0.9) },
});

export const createAmplifyAuthTheme = (
  muiTheme: MuiTheme,
): ReturnType<typeof createAmplifyTheme> => {
  const isDarkMode = muiTheme.palette.mode === "dark";
  const borderRadius = Number(muiTheme.shape.borderRadius);
  const primaryMain = muiTheme.palette.primary.main;
  const secondaryMain = muiTheme.palette.secondary.main;
  const successMain = muiTheme.palette.success.main;
  const warningMain = muiTheme.palette.warning.main;
  const errorMain = muiTheme.palette.error.main;

  const surfaceBorder = alpha(
    isDarkMode ? "#FFFFFF" : "#000000",
    isDarkMode ? 0.08 : 0.08,
  );
  const secondaryBorder = alpha(
    isDarkMode ? "#FFFFFF" : "#000000",
    isDarkMode ? 0.14 : 0.12,
  );
  const tertiaryBorder = alpha(
    isDarkMode ? "#FFFFFF" : "#000000",
    isDarkMode ? 0.22 : 0.18,
  );
  const focusColor = alpha(primaryMain, isDarkMode ? 0.72 : 0.5);
  const mutedSurface = isDarkMode
    ? alpha("#FFFFFF", 0.04)
    : alpha("#6B645A", 0.08);
  const raisedSurface = isDarkMode
    ? alpha("#FFFFFF", 0.06)
    : alpha("#FFFFFF", 0.92);
  const disabledSurface = isDarkMode
    ? alpha("#FFFFFF", 0.08)
    : alpha("#000000", 0.05);
  const successBackground = alpha(successMain, isDarkMode ? 0.18 : 0.1);
  const warningBackground = alpha(warningMain, isDarkMode ? 0.18 : 0.12);
  const errorBackground = alpha(errorMain, isDarkMode ? 0.18 : 0.12);
  const infoBackground = alpha(primaryMain, isDarkMode ? 0.18 : 0.1);

  return createAmplifyTheme({
    name: `mtg-deck-builder-auth-${muiTheme.palette.mode}`,
    primaryColor: "blue",
    secondaryColor: "red",
    tokens: {
      fonts: {
        default: {
          static: { value: bodyFontFamily },
          variable: { value: bodyFontFamily },
        },
      },
      radii: {
        small: { value: `${borderRadius}px` },
        medium: { value: `${borderRadius}px` },
        large: { value: `${borderRadius + 4}px` },
        xl: { value: "999px" },
      },
      colors: {
        blue: createColorScale(primaryMain, isDarkMode),
        red: createColorScale(secondaryMain, isDarkMode),
        green: createColorScale(successMain, isDarkMode),
        orange: createColorScale(warningMain, isDarkMode),
        neutral: createNeutralScale(isDarkMode),
        overlay: createOverlayScale(isDarkMode),
        background: {
          primary: { value: muiTheme.palette.background.paper },
          secondary: { value: mutedSurface },
          tertiary: { value: raisedSurface },
          disabled: { value: disabledSurface },
          info: { value: infoBackground },
          success: { value: successBackground },
          warning: { value: warningBackground },
          error: { value: errorBackground },
        },
        border: {
          primary: { value: surfaceBorder },
          secondary: { value: secondaryBorder },
          tertiary: { value: tertiaryBorder },
          focus: { value: focusColor },
          disabled: { value: tertiaryBorder },
          error: { value: errorMain },
        },
        font: {
          primary: { value: muiTheme.palette.text.primary },
          secondary: { value: muiTheme.palette.text.secondary },
          tertiary: {
            value: alpha(
              muiTheme.palette.text.primary,
              isDarkMode ? 0.72 : 0.62,
            ),
          },
          inverse: { value: "#F8FAFC" },
          interactive: { value: primaryMain },
          hover: { value: darken(primaryMain, isDarkMode ? 0.02 : 0.08) },
          focus: { value: primaryMain },
          active: { value: darken(primaryMain, isDarkMode ? 0.08 : 0.12) },
          disabled: {
            value: alpha(
              muiTheme.palette.text.primary,
              isDarkMode ? 0.42 : 0.38,
            ),
          },
          info: { value: primaryMain },
          success: { value: successMain },
          warning: { value: warningMain },
          error: { value: errorMain },
        },
      },
      components: {
        authenticator: {
          maxWidth: { value: "100%" },
          container: {
            widthMax: { value: "100%" },
          },
          router: {
            backgroundColor: { value: "transparent" },
            borderColor: { value: "transparent" },
            borderWidth: { value: "0" },
            borderStyle: { value: "solid" },
            boxShadow: { value: "none" },
          },
          form: {
            padding: { value: "0" },
          },
          footer: {
            paddingBottom: { value: "0" },
          },
          state: {
            inactive: {
              backgroundColor: { value: mutedSurface },
            },
          },
          orContainer: {
            color: { value: muiTheme.palette.text.secondary },
            orLine: {
              backgroundColor: { value: secondaryBorder },
            },
          },
        },
        alert: {
          backgroundColor: { value: raisedSurface },
          color: { value: muiTheme.palette.text.primary },
          paddingBlock: { value: muiTheme.spacing(1.25) },
          paddingInline: { value: muiTheme.spacing(1.5) },
          info: {
            color: { value: primaryMain },
            backgroundColor: { value: infoBackground },
          },
          success: {
            color: { value: successMain },
            backgroundColor: { value: successBackground },
          },
          warning: {
            color: { value: warningMain },
            backgroundColor: { value: warningBackground },
          },
          error: {
            color: { value: errorMain },
            backgroundColor: { value: errorBackground },
          },
        },
        button: {
          fontWeight: { value: 600 },
          borderRadius: { value: `${borderRadius}px` },
          primary: {
            backgroundColor: { value: primaryMain },
            _hover: {
              backgroundColor: { value: darken(primaryMain, 0.08) },
            },
            _focus: {
              backgroundColor: { value: darken(primaryMain, 0.08) },
              boxShadow: createFocusShadow(focusColor),
            },
            _active: {
              backgroundColor: { value: darken(primaryMain, 0.16) },
            },
          },
        },
        field: {
          label: {
            color: { value: muiTheme.palette.text.secondary },
          },
        },
        fieldcontrol: {
          borderRadius: { value: `${borderRadius}px` },
          borderColor: { value: secondaryBorder },
          color: { value: muiTheme.palette.text.primary },
          _focus: {
            borderColor: { value: primaryMain },
            boxShadow: createFocusShadow(focusColor),
          },
          _disabled: {
            color: {
              value: alpha(
                muiTheme.palette.text.primary,
                isDarkMode ? 0.42 : 0.38,
              ),
            },
            borderColor: { value: tertiaryBorder },
            backgroundColor: { value: disabledSurface },
            cursor: { value: "not-allowed" },
          },
          _error: {
            borderColor: { value: errorMain },
            color: { value: muiTheme.palette.text.primary },
            _focus: {
              boxShadow: createFocusShadow(alpha(errorMain, 0.48)),
            },
          },
          info: {
            _focus: {
              boxShadow: createFocusShadow(focusColor),
            },
          },
          warning: {
            _focus: {
              boxShadow: createFocusShadow(alpha(warningMain, 0.48)),
            },
          },
          success: {
            _focus: {
              boxShadow: createFocusShadow(alpha(successMain, 0.48)),
            },
          },
        },
        heading: {
          color: { value: muiTheme.palette.text.primary },
        },
        input: {
          color: { value: muiTheme.palette.text.primary },
          borderColor: { value: secondaryBorder },
          _focus: {
            borderColor: { value: primaryMain },
          },
        },
        link: {
          color: { value: primaryMain },
          hover: { color: { value: darken(primaryMain, 0.08) } },
          focus: { color: { value: darken(primaryMain, 0.08) } },
          active: { color: { value: darken(primaryMain, 0.14) } },
          visited: { color: { value: primaryMain } },
        },
        tabs: {
          backgroundColor: { value: "transparent" },
          borderColor: { value: "transparent" },
          borderStyle: { value: "solid" },
          borderWidth: { value: "0" },
          gap: { value: muiTheme.spacing(1) },
          item: {
            backgroundColor: {
              value: isDarkMode
                ? alpha("#FFFFFF", 0.03)
                : alpha("#000000", 0.03),
            },
            borderColor: { value: secondaryBorder },
            borderStyle: { value: "solid" },
            borderWidth: { value: "1px" },
            color: { value: muiTheme.palette.text.secondary },
            fontSize: { value: muiTheme.typography.body2.fontSize as string },
            fontWeight: { value: 600 },
            paddingHorizontal: { value: muiTheme.spacing(1.75) },
            paddingVertical: { value: muiTheme.spacing(1.25) },
            textAlign: { value: "center" },
            transitionDuration: {
              value: muiTheme.transitions.duration.shorter.toString() + "ms",
            },
            _hover: {
              color: { value: muiTheme.palette.text.primary },
              backgroundColor: { value: mutedSurface },
              borderColor: { value: tertiaryBorder },
            },
            _focus: {
              color: { value: muiTheme.palette.text.primary },
              backgroundColor: { value: mutedSurface },
              borderColor: { value: primaryMain },
              boxShadow: createFocusShadow(focusColor),
            },
            _active: {
              color: { value: muiTheme.palette.text.primary },
              backgroundColor: {
                value: alpha(primaryMain, isDarkMode ? 0.16 : 0.1),
              },
              borderColor: {
                value: alpha(primaryMain, isDarkMode ? 0.42 : 0.28),
              },
              boxShadow: { value: "none" },
            },
          },
          panel: {
            backgroundColor: { value: "transparent" },
            paddingBlock: { value: muiTheme.spacing(2.5) },
            paddingInline: { value: "0" },
          },
        },
        text: {
          color: { value: muiTheme.palette.text.primary },
          primary: { color: { value: muiTheme.palette.text.primary } },
          secondary: { color: { value: muiTheme.palette.text.secondary } },
          info: { color: { value: primaryMain } },
          success: { color: { value: successMain } },
          warning: { color: { value: warningMain } },
          error: { color: { value: errorMain } },
        },
      },
    },
  } as any);
};
